import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, LayoutGrid, Table2, Lock, CheckCircle2, MapPin, AlertTriangle, UserCheck } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { RowActions } from "@/components/RowActions";
import { SearchBox } from "@/components/SearchBox";
import { BulkBar } from "@/components/BulkBar";
import { Checkbox } from "@/components/ui/checkbox";
import { usePublicSettings } from "@/context/PublicSettingsContext";
import { RENTAL_LABELS } from "@/lib/helpers";
import { useAuth } from "@/context/AuthContext";
import { STAGES, currentStage, fmtDate } from "@/lib/helpers";
import { OnboardingFields } from "@/components/OnboardingForm";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const BOARD_COLS = [...STAGES, "Live"];
const COL_ACCENT = {
  "Property Sourcing & Initial Chat": "border-t-[#3a4a6b]",
  "Refurbishment": "border-t-[#8a6d3b]",
  "Compliance & Licensing": "border-t-[#D98324]",
  "Operational Setup": "border-t-[#C6A87C]",
  Live: "border-t-[#2B4C3F]",
};

function CreatePropertyDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [landlords, setLandlords] = useState([]);
  const { features } = usePublicSettings();
  const modes = [
    features.holiday_rentals !== false && "holiday",
    features.long_term === true && "long_term",
  ].filter(Boolean);
  const [form, setForm] = useState({
    title: "", address: "", landlord_id: "", rental_type: "holiday", property_type: "Apartment",
    bedrooms: 1, bathrooms: 1, council_tax_band: "", commission_pct: "", description: "",
    contractor_name: "", contractor_email: "",
  });
  const [intake, setIntake] = useState({});
  const [directors, setDirectors] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/landlords").then((r) => setLandlords(r.data));
    api.get("/settings").then((r) => setForm((f) => ({ ...f, commission_pct: f.commission_pct === "" ? (r.data.default_commission ?? "") : f.commission_pct })));
  }, [open]);
  useEffect(() => {
    if (modes.length && !modes.includes(form.rental_type)) setForm((f) => ({ ...f, rental_type: modes[0] }));
  }, [features]);

  const pickOwner = (v) => {
    const l = landlords.find((x) => x.id === v) || {};
    setForm((f) => ({ ...f, landlord_id: v, commission_pct: l.commission_pct != null ? l.commission_pct : f.commission_pct }));
    setIntake((prev) => ({
      ...prev,
      owner_name: prev.owner_name || l.name || "",
      owner_email: prev.owner_email || l.email || "",
      owner_mobile: prev.owner_mobile || l.phone || "",
      owner_home_address: prev.owner_home_address || l.address || "",
    }));
  };

  const submit = async () => {
    try {
      const { data } = await api.post("/properties", {
        ...form, bedrooms: Number(form.bedrooms), bathrooms: Number(form.bathrooms),
        commission_pct: form.commission_pct === "" ? null : Number(form.commission_pct), intake, directors,
      });
      toast.success("Property created — onboarding started");
      setOpen(false);
      onCreated(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-property-button"><Plus className="h-4 w-4 mr-1" /> New Property</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle className="font-head">Add Property</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto thin-scroll pr-1">
          <div>
            <Label className="overline">Title</Label>
            <Input data-testid="prop-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label className="overline">Address</Label>
            <Input data-testid="prop-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label className="overline">Owner</Label>
            <Select value={form.landlord_id} onValueChange={pickOwner}>
              <SelectTrigger data-testid="prop-landlord-select" className="mt-1.5"><SelectValue placeholder="Select landlord" /></SelectTrigger>
              <SelectContent>
                {landlords.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {landlords.length === 0 && <p className="text-xs text-[#D98324] mt-1">Add a landlord first.</p>}
          </div>
          {modes.length > 1 && (
            <div>
              <Label className="overline">Rental type</Label>
              <Select value={form.rental_type} onValueChange={(v) => setForm({ ...form, rental_type: v })}>
                <SelectTrigger data-testid="prop-rental-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {modes.map((m) => <SelectItem key={m} value={m}>{RENTAL_LABELS[m]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="overline">Type</Label>
              <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Apartment", "House", "Maisonette", "Studio"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Beds</Label>
              <Input type="number" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="overline">Baths</Label>
              <Input type="number" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} className="mt-1.5" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Council Tax Band</Label>
              <Input value={form.council_tax_band} onChange={(e) => setForm({ ...form, council_tax_band: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="overline">Commission %</Label>
              <Input data-testid="prop-commission-input" type="number" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} className="mt-1.5" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Contractor / Agent name</Label>
              <Input data-testid="prop-contractor-name" value={form.contractor_name} onChange={(e) => setForm({ ...form, contractor_name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="overline">Contractor email</Label>
              <Input data-testid="prop-contractor-email" value={form.contractor_email} onChange={(e) => setForm({ ...form, contractor_email: e.target.value })} className="mt-1.5" placeholder="For expiry reminders" />
            </div>
          </div>
          <div>
            <Label className="overline">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" />
          </div>

          <div className="pt-2 border-t border-border">
            <p className="overline text-muted-foreground mb-3">New client onboarding questionnaire</p>
            <OnboardingFields intake={intake} setIntake={setIntake} directors={directors} setDirectors={setDirectors} mode="staff" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="save-property-button" onClick={submit} disabled={!form.title || !form.address || !form.landlord_id}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Properties() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [props, setProps] = useState([]);
  const [sel, setSel] = useState([]);
  const toggleSel = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const [view, setView] = useState("board");
  const [showArchived, setShowArchived] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [q, setQ] = useState("");
  const [mine, setMine] = useState(false);
  const isStaff = ["admin", "operations", "compliance"].includes(user.role);

  const load = () => api.get("/properties", { params: { include_archived: showArchived, q, manager: mine ? "me" : "" } }).then((r) => setProps(r.data));
  useEffect(() => { load(); }, [showArchived, q, mine]);

  const canManage = ["admin", "operations"].includes(user.role);

  const moveTo = async (id, stage) => {
    try { await api.patch(`/properties/${id}/board-stage`, { stage }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleAttention = async (p) => {
    let reason;
    if (!p.needs_attention) {
      reason = window.prompt("Why does this property need attention? (optional)") || "";
    }
    try { await api.patch(`/properties/${p.id}/attention`, { needs_attention: !p.needs_attention, reason }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-accent">Gatekeeper</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tight mt-1">Properties</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SearchBox testId="properties-search" placeholder="Search name, address, owner, ref…" onSearch={setQ} />
          {isStaff && (
            <Button variant={mine ? "default" : "outline"} size="sm" data-testid="my-properties-toggle" onClick={() => setMine(!mine)}>
              <UserCheck className="h-4 w-4 mr-1" />My properties
            </Button>
          )}
          <div className="flex border border-border rounded-md overflow-hidden">
            <button data-testid="board-view-btn" onClick={() => setView("board")} className={`px-3 py-2 flex items-center gap-1.5 text-sm ${view === "board" ? "bg-[#18181A] text-white" : "bg-white"}`}><LayoutGrid className="h-4 w-4" /> Board</button>
            <button data-testid="table-view-btn" onClick={() => setView("table")} className={`px-3 py-2 flex items-center gap-1.5 text-sm ${view === "table" ? "bg-[#18181A] text-white" : "bg-white"}`}><Table2 className="h-4 w-4" /> Table</button>
          </div>
          {canManage && (
          <Button variant="outline" size="sm" data-testid="toggle-archived-properties" onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? "Hide archived" : "Show archived"}
          </Button>
          )}
          {canManage && <CreatePropertyDialog onCreated={load} />}
        </div>
      </div>

      {view === "board" ? (
        <>
          {q && props.length === 0 && <p data-testid="properties-board-empty" className="text-sm text-muted-foreground">No properties match "{q}".</p>}
          {canManage && <p className="text-xs text-muted-foreground -mt-2">Cards move automatically as tasks complete. Drag a card to a different column to reprioritise it manually.</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          {BOARD_COLS.map((col) => {
            const items = props.filter((p) => currentStage(p) === col);
            const isDropCol = col !== "Live" && canManage;
            return (
              <div key={col}
                onDragOver={isDropCol ? (e) => e.preventDefault() : undefined}
                onDrop={isDropCol ? (e) => { e.preventDefault(); if (dragId) { moveTo(dragId, col); setDragId(null); } } : undefined}
                data-testid={`board-col-${col}`}
                className={`bg-secondary/40 border border-border border-t-2 ${COL_ACCENT[col]} rounded-md p-3 min-h-[200px] transition-shadow ${isDropCol && dragId ? "ring-2 ring-[#C6A87C]/60" : ""}`}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <span className="overline text-[0.6rem]">{col}</span>
                  <span className="text-xs font-mono text-muted-foreground">{items.length}</span>
                </div>
                <div className="space-y-2">
                  {items.map((p) => (
                    <motion.div
                      key={p.id}
                      layout
                      draggable={canManage && p.status !== "live"}
                      onDragStart={() => setDragId(p.id)}
                      onDragEnd={() => setDragId(null)}
                      data-testid={`board-card-${p.id}`}
                      onClick={() => nav(`/properties/${p.id}`)}
                      className={`relative bg-white border rounded-md p-3 cursor-pointer hover:-translate-y-0.5 hover:border-[#C6A87C] transition-all ${p.needs_attention ? "border-l-4 border-l-[#9E382A] border-border" : "border-border"} ${p.archived ? "opacity-60" : ""} ${dragId === p.id ? "opacity-40" : ""}`}
                    >
                      {canManage && <div className="absolute top-1.5 right-1.5" onClick={(e) => e.stopPropagation()}><RowActions entity="properties" id={p.id} archived={p.archived} name={p.title} canDelete={user.role === "admin"} onDone={load} /></div>}
                      {p.needs_attention && <span data-testid={`attention-badge-${p.id}`} className="inline-flex items-center gap-1 text-[0.6rem] px-1.5 py-0.5 rounded-full bg-[#9E382A]/12 text-[#9E382A] font-semibold mb-1"><AlertTriangle className="h-2.5 w-2.5" />Needs attention</span>}
                      <p className="text-sm font-semibold leading-tight pr-7">{p.title}</p>
                      {p.needs_attention && p.needs_attention_reason && <p className="text-[0.65rem] text-[#9E382A] mt-0.5">{p.needs_attention_reason}</p>}
                      {p.archived && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">Archived</span>}
                      <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" />{p.address}</p>
                      {p.board_stage && p.status !== "live" && (
                        <button data-testid={`board-reset-${p.id}`} onClick={(e) => { e.stopPropagation(); moveTo(p.id, "auto"); }} className="text-[0.6rem] text-[#8a6d3b] underline mt-1">Moved manually · reset to auto</button>
                      )}
                      {canManage && (
                        <button data-testid={`attention-toggle-${p.id}`} onClick={(e) => { e.stopPropagation(); toggleAttention(p); }}
                          className={`block text-[0.6rem] mt-1 underline ${p.needs_attention ? "text-muted-foreground" : "text-[#9E382A]"}`}>
                          {p.needs_attention ? "Clear flag" : "Flag: needs attention"}
                        </button>
                      )}
                      <div className="mt-2.5">
                        <Progress value={p.progress.pct} className="h-1.5" />
                        <div className="flex justify-between mt-1 text-[0.65rem] font-mono text-muted-foreground">
                          <span>{p.progress.completed}/{p.progress.total}</span>
                          {p.status === "live"
                            ? <span className="text-[#2B4C3F] flex items-center gap-0.5"><CheckCircle2 className="h-3 w-3" />Live</span>
                            : <span>{p.progress.pct}%</span>}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            );
          })}
          </div>
        </>
      ) : (
        <div className="bg-white border border-border rounded-md overflow-hidden">
          {canManage && sel.length > 0 && <div className="p-2"><BulkBar ids={sel} onClear={() => setSel([])} onDone={() => { setSel([]); load(); }} /></div>}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/40 text-left">
                {canManage && <th className="pl-4 py-3 w-8"><Checkbox data-testid="select-all-properties" checked={props.length > 0 && sel.length === props.length} onCheckedChange={(c) => setSel(c ? props.map((p) => p.id) : [])} /></th>}
                <th className="px-4 py-3 overline font-semibold">Property</th>
                <th className="px-4 py-3 overline font-semibold">Owner</th>
                <th className="px-4 py-3 overline font-semibold">Manager</th>
                <th className="px-4 py-3 overline font-semibold">Stage</th>
                <th className="px-4 py-3 overline font-semibold w-48">Progress</th>
                <th className="px-4 py-3 overline font-semibold">Status</th>
                {canManage && <th className="px-4 py-3 overline font-semibold w-12"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {props.map((p) => (
                <tr key={p.id} data-testid={`row-${p.id}`} onClick={() => nav(`/properties/${p.id}`)} className={`cursor-pointer hover:bg-secondary/40 transition-colors ${p.archived ? "opacity-60" : ""} ${sel.includes(p.id) ? "bg-[#C6A87C]/10" : ""}`}>
                  {canManage && <td className="pl-4 py-3" onClick={(e) => e.stopPropagation()}><Checkbox data-testid={`select-property-${p.id}`} checked={sel.includes(p.id)} onCheckedChange={() => toggleSel(p.id)} /></td>}
                  <td className="px-4 py-3">
                    <p className="font-medium flex items-center gap-2">{p.title}
                      {p.needs_attention && <span data-testid={`table-attention-${p.id}`} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#9E382A]/12 text-[#9E382A] font-semibold"><AlertTriangle className="h-2.5 w-2.5" />Attention</span>}
                      {p.archived && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">Archived</span>}</p>
                    <p className="text-xs text-muted-foreground">{p.address}</p>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{p.landlord_name}</td>
                  <td data-testid={`manager-${p.id}`} className="px-4 py-3 text-muted-foreground">{p.manager_name || "—"}</td>
                  <td className="px-4 py-3">{currentStage(p)}</td>
                  <td className="px-4 py-3">
                    <Progress value={p.progress.pct} className="h-1.5" />
                    <span className="text-xs font-mono text-muted-foreground">{p.progress.pct}%</span>
                  </td>
                  <td className="px-4 py-3">
                    {p.status === "live"
                      ? <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#2B4C3F]/12 text-[#2B4C3F]"><CheckCircle2 className="h-3 w-3" />Live</span>
                      : <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-secondary text-muted-foreground"><Lock className="h-3 w-3" />Onboarding</span>}
                  </td>
                  {canManage && <td className="px-4 py-3 text-right"><RowActions entity="properties" id={p.id} archived={p.archived} name={p.title} canDelete={user.role === "admin"} onDone={load} /></td>}
                </tr>
              ))}
            </tbody>
          </table>
          {props.length === 0 && <p data-testid="properties-empty" className="p-10 text-center text-sm text-muted-foreground">{q ? `No properties match "${q}".` : mine ? "No properties are assigned to you yet." : "No properties yet."}</p>}
        </div>
      )}
    </div>
  );
}
